import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PlaylistManager from './PlaylistManager';
import PlaytreeManager from '../PlaytreeManager/PlaytreeManager';
import useDeepLinkedSection from '../navigation/useDeepLinkedSection';
import DashboardSection from '../components/DashboardSection';
import './PlaylistAssign.css';
import './CuratorPage.css';

// Merged Curator page (DJB-61): PlaylistManager and PlaytreeManager combined
// as two collapsible sections on the Dashboard's borderless shell (DJB-160).
// Each section opens and closes independently and both start collapsed; a
// collapsed header carries a muted info line that disappears once the
// section is expanded (DJB-83).
const CuratorPage = () => {
  const [searchParams] = useSearchParams();

  const playlistId = searchParams.get('playlistId');
  const playtreeId = searchParams.get('playtreeId');

  // Default: everything collapsed. Deep links are explicit navigation, so
  // they pre-open their section; a playlistId wins over playtreeId (same
  // precedence the old tab-switching effect used).
  const [openSections, setOpenSections] = useState(() => {
    if (playlistId) return new Set(['playlist']);
    if (playtreeId) return new Set(['category']);
    return new Set();
  });

  const openSection = (key) =>
    setOpenSections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));

  const toggleSection = (key) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  useEffect(() => {
    if (playlistId) {
      openSection('playlist');
    } else if (playtreeId) {
      openSection('category');
    }
  }, [playlistId, playtreeId]);

  // DJB-112: a section picked from the left nav arrives as ?section=<anchor>
  // and must open even though sections default to collapsed
  useDeepLinkedSection(
    {
      'section-playlist-editor': 'playlist',
      'section-category-editor': 'category',
    },
    openSection
  );

  // DJB-65: clicking a playlist inside a category opens it in the Playlist
  // Editor section. A fresh request object per click means the same playlist
  // can be re-opened after the user selected something else in between.
  // DJB-215: a ?playlistId deep link (e.g. "Open in Studio" on Playlist
  // Subscriptions) is fed through the same request so the linked playlist is
  // selected from the router's own params, not just window.location.
  const [openPlaylistRequest, setOpenPlaylistRequest] = useState(() =>
    playlistId ? { id: playlistId } : null
  );
  useEffect(() => {
    if (playlistId) {
      setOpenPlaylistRequest({ id: playlistId });
    }
  }, [playlistId]);
  const handleOpenPlaylist = (id) => {
    setOpenPlaylistRequest({ id });
    openSection('playlist');
  };

  const renderSection = ({ key, title, info, bodyId, anchorId, children }) => (
    <DashboardSection
      id={anchorId}
      title={title}
      info={info}
      isOpen={openSections.has(key)}
      onToggle={() => toggleSection(key)}
      bodyId={bodyId}
    >
      {children}
    </DashboardSection>
  );

  return (
    <div className="ps-page ps-page--curator">
      {renderSection({
        key: 'playlist',
        title: 'Playlist Editor',
        info: 'Bewerk bestaande playlists of maak een nieuwe aan.',
        bodyId: 'ps-body-playlist-curator',
        anchorId: 'section-playlist-editor',
        children: <PlaylistManager openPlaylistRequest={openPlaylistRequest} />,
      })}
      {renderSection({
        key: 'category',
        title: 'Category Editor',
        info: 'Bewerk de inhoud van categorieën of maak een nieuwe aan.',
        bodyId: 'ps-body-category-curator',
        anchorId: 'section-category-editor',
        children: (
          <PlaytreeManager playtreeId={playtreeId} onOpenPlaylist={handleOpenPlaylist} />
        ),
      })}
    </div>
  );
};

export default CuratorPage;
